import {
  dapiFetch,
  dapiFetchCache,
  ErrorFetch,
  IDapiCacheRequest,
  IDapiCacheResponse,
  IDapiRequest,
  IDapiResponse,
} from '@origin-digital/daxi';
import { logger } from '@origin-digital/reporting-client';
import { getHeaderTraceId, getSessionTraceId } from '../traceId-generator/traceId-generator';
import { LOG_TRACE_HEADER } from '../traceId-generator/logging';
import { redactUrl } from './redact-url';

const applyCommonDaxiConfig = (config: IDapiRequest): { businessTxID: string; traceId: string } => {
  const businessTxID = getSessionTraceId();
  const traceHeader = getHeaderTraceId();
  const traceId = traceHeader[LOG_TRACE_HEADER.TRACE_ID];
  config.businessTxID = businessTxID;
  config.headers = Object.assign({}, config.headers, traceHeader);
  return { businessTxID, traceId };
};

const logDaxiErrorAndRethrow =
  (url: string, businessTxID: string, traceId: string) =>
  <F = undefined>(error: ErrorFetch<F>) => {
    logger.error(
      error.message || error.displayMessage || 'API call failed with no specified message',
      {
        url: redactUrl(url),
        status: error.fetchResponse?.status,
        [LOG_TRACE_HEADER.BUSINESS_TX_ID]: businessTxID,
        [LOG_TRACE_HEADER.TRACE_ID]: traceId,
      }
    );
    throw error;
  };

/**
 * `dapiFetch` with NR breadcrumbs: injects trace headers, info-logs each call,
 * error-logs on rejection then rethrows.
 */
export const dapiFetchWithLogging = <M, F = undefined>(
  url: string,
  config: IDapiRequest = {}
): Promise<IDapiResponse<M>> => {
  const { businessTxID, traceId } = applyCommonDaxiConfig(config);
  logger.info(
    `DAPI fetch for ${redactUrl(url)} with business txn id ${businessTxID} and traceId ${traceId}`
  );
  return dapiFetch<M, F>(url, config).catch(logDaxiErrorAndRethrow(url, businessTxID, traceId));
};

/**
 * `dapiFetchCache` with trace headers + error log only — no per-call info log.
 * Cache reads fire many times per mount (atom subscribers, re-renders) and
 * the wrapper can't tell a hit from a network miss, so info-logging here
 * would flood NR with entries that don't map to actual HTTP traffic.
 */
export const dapiFetchCacheWithLogging = <M>(
  url: string,
  config: IDapiCacheRequest = { ttlInMin: 30 }
): Promise<IDapiCacheResponse<M>> => {
  const { businessTxID, traceId } = applyCommonDaxiConfig(config);
  return dapiFetchCache<M>(url, config).catch(logDaxiErrorAndRethrow(url, businessTxID, traceId));
};
